import { types, Instance, flow, getRoot } from 'mobx-state-tree';
import api from '../services/api';

/**
 * Session - Represents a single active session with metadata
 */
export const Session = types.model('Session', {
  sessionId: types.identifier,
  userId: types.string,
  ip: types.optional(types.string, 'unknown'),
  userAgent: types.optional(types.string, 'unknown'),
  country: types.maybeNull(types.string),
  region: types.maybeNull(types.string),
  city: types.maybeNull(types.string),
  timezone: types.maybeNull(types.string),
  connectedAt: types.optional(types.number, 0),
  lastPing: types.optional(types.number, 0),
})
  .views(self => ({
    get locationString() {
      const parts = [self.city, self.region, self.country].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : 'Unknown location';
    },
    
    get browserName() {
      const ua = self.userAgent.toLowerCase();
      if (ua.includes('chrome')) return 'Chrome';
      if (ua.includes('safari')) return 'Safari';
      if (ua.includes('firefox')) return 'Firefox';
      if (ua.includes('edge')) return 'Edge';
      return 'Unknown';
    },
    
    get deviceType() {
      const ua = self.userAgent.toLowerCase();
      if (ua.includes('mobile')) return 'Mobile';
      if (ua.includes('tablet')) return 'Tablet';
      return 'Desktop';
    },
  }));

/**
 * OnlineUser - Represents a user's online/offline status and history
 */
export const OnlineUser = types.model('OnlineUser', {
  userId: types.identifier,
  email: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  firstName: types.maybeNull(types.string),
  lastName: types.maybeNull(types.string),
  avatar: types.maybeNull(types.string),
  cursorColor: types.optional(types.string, '#4A9EFF'),
  currentMindmapId: types.maybeNull(types.string), // Which mindmap user is viewing
  lastSeen: types.maybeNull(types.Date),
  sessionCount: types.optional(types.number, 0),
  totalDurationSeconds: types.optional(types.number, 0),
  isOnline: types.optional(types.boolean, false),
  sessions: types.array(Session),
}).views(self => ({
  get displayName() {
    if (self.firstName && self.lastName) {
      return `${self.firstName} ${self.lastName}`;
    }
    if (self.name) {
      return self.name;
    }
    if (self.email) {
      return self.email.split('@')[0];
    }
    return 'User';
  },
  
  get lastSeenText() {
    if (!self.lastSeen) return 'Never';
    
    const now = new Date().getTime();
    const lastSeenTime = new Date(self.lastSeen).getTime();
    const diffMs = now - lastSeenTime;
    const diffMinutes = Math.floor(diffMs / 60000);
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  },
}));

/**
 * OnlinePresenceStore - Tracks online users and presence history
 * 
 * Responsibilities:
 * - Maintain list of currently online users (from WebSocket connections)
 * - Fetch and cache presence history (last 30 days)
 * - Update online status when users join/leave via WebSocket
 * - Provide sorted views of users by last seen
 * 
 * Integration:
 * - Listens to WorkspaceRealtimeStore for presence:user:joined/left events
 * - Fetches historical data from backend REST API
 * - Updates via MobX actions only (no direct mutations)
 */
export const OnlinePresenceStore = types
  .model('OnlinePresenceStore', {
    users: types.map(OnlineUser),
    isLoading: types.optional(types.boolean, false),
    lastFetchTime: types.maybeNull(types.Date),
    fetchError: types.maybeNull(types.string),
    mindmapViewers: types.map(types.array(types.string)), // mindmapId -> [userId]
  })
  .views(self => ({
    get onlineUsers() {
      return Array.from(self.users.values())
        .filter(u => u.isOnline)
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
    },
    
    /**
     * Get users currently viewing a specific mindmap
     */
    getUsersOnMindmap(mindmapId: string) {
      const viewerIds = self.mindmapViewers.get(mindmapId) || [];
      return viewerIds
        .map(userId => self.users.get(userId))
        .filter(user => user !== undefined);
    },
    
    get allUsersHistory() {
      return Array.from(self.users.values())
        .sort((a, b) => {
          // Online users first
          if (a.isOnline && !b.isOnline) return -1;
          if (!a.isOnline && b.isOnline) return 1;
          
          // Then by last seen (most recent first)
          const aTime = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
          const bTime = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
          return bTime - aTime;
        });
    },
    
    get onlineCount() {
      return this.onlineUsers.length;
    },
    
    getUserById(userId: string) {
      return self.users.get(userId);
    },
  }))
  .actions(self => ({
    /**
     * Fetch presence history from backend
     * Called on app initialization or when switching organizations
     */
    fetchPresenceHistory: flow(function* (days: number = 30) {
      const rootStore = getRoot(self) as any;
      const orgId = rootStore.organization?.organizationId;
      
      if (!orgId) {
        console.warn('[OnlinePresence] Cannot fetch without organizationId');
        return;
      }
      
      self.isLoading = true;
      self.fetchError = null;
      
      try {
        // Use api service which includes JWT interceptor
        const response = yield api.get(`/api/presence/history?days=${days}`);
        
        const { users } = response.data;
        
        // Update users in store via action
        (self as any).updateUsersFromHistory(users);
        
        self.lastFetchTime = new Date();
        console.log(`✅ [OnlinePresence] Fetched ${users.length} users from history`);
      } catch (error: any) {
        console.error('[OnlinePresence] Failed to fetch history:', error);
        self.fetchError = error.message;
      } finally {
        self.isLoading = false;
      }
    }),
    
    /**
     * Update users from history API response
     * MobX action to prevent direct mutations
     */
    updateUsersFromHistory(users: any[]) {
      users.forEach(userData => {
        const existing = self.users.get(userData.userId);
        
        if (existing) {
          // Update existing user (don't override isOnline, that comes from WebSocket)
          existing.email = userData.email || existing.email;
          existing.name = userData.name || existing.name;
          existing.firstName = userData.firstName || existing.firstName;
          existing.lastName = userData.lastName || existing.lastName;
          existing.avatar = userData.avatar || existing.avatar;
          existing.cursorColor = userData.cursorColor || existing.cursorColor;
          existing.lastSeen = userData.lastSeen ? new Date(userData.lastSeen) : existing.lastSeen;
          existing.sessionCount = userData.sessionCount || existing.sessionCount;
          existing.totalDurationSeconds = userData.totalDurationSeconds || existing.totalDurationSeconds;
        } else {
          // Add new user
          self.users.put({
            userId: userData.userId,
            email: userData.email,
            name: userData.name,
            firstName: userData.firstName,
            lastName: userData.lastName,
            avatar: userData.avatar,
            cursorColor: userData.cursorColor || '#4A9EFF',
            lastSeen: userData.lastSeen ? new Date(userData.lastSeen) : null,
            sessionCount: userData.sessionCount || 0,
            totalDurationSeconds: userData.totalDurationSeconds || 0,
            isOnline: false, // Will be updated by WebSocket events
          });
        }
      });
    },
    
    /**
     * Mark a user as online
     * Called when presence:user:joined event received
     */
    setUserOnline(userId: string, userData?: any) {
      let user = self.users.get(userId);
      
      if (user) {
        user.isOnline = true;
        user.lastSeen = new Date();
      }
      // User will be created by updateSessions() with full session data
    },
    
    /**
     * Mark a user as offline
     * Called when presence:user:left event received
     */
    setUserOffline(userId: string) {
      const user = self.users.get(userId);
      
      if (user) {
        user.isOnline = false;
        user.lastSeen = new Date();
        user.currentMindmapId = null; // Clear mindmap when going offline
        console.log(`👋 [OnlinePresence] User ${userId} went offline`);
      }
    },
    
    /**
     * Update mindmap viewers list
     * Called when receiving presence:mindmap:viewers event
     */
    updateMindmapViewers(mindmapId: string, viewers: Array<{ userId: string; userName: string; userColor: string }>) {
      // Update the viewers map
      self.mindmapViewers.set(mindmapId, viewers.map(v => v.userId));
      
      // Update each user's currentMindmapId and cursorColor
      viewers.forEach(viewer => {
        let user = self.users.get(viewer.userId);
        
        if (!user) {
          // Create user if doesn't exist
          user = self.users.put({
            userId: viewer.userId,
            email: null,
            name: viewer.userName,
            firstName: null,
            lastName: null,
            avatar: null,
            cursorColor: viewer.userColor,
            currentMindmapId: mindmapId,
            lastSeen: new Date(),
            sessionCount: 0,
            totalDurationSeconds: 0,
            isOnline: true,
            sessions: [],
          });
        } else {
          // Update existing user
          user.currentMindmapId = mindmapId;
          user.cursorColor = viewer.userColor;
          if (viewer.userName && !user.name) {
            user.name = viewer.userName;
          }
        }
      });
      
      // Clear currentMindmapId for users who are no longer viewing this mindmap
      self.users.forEach(user => {
        if (user.currentMindmapId === mindmapId && 
            !viewers.find(v => v.userId === user.userId)) {
          user.currentMindmapId = null;
        }
      });
      
      console.log(`👁️  [OnlinePresence] Updated viewers for mindmap ${mindmapId}: ${viewers.length} users`);
    },
    
    /**
     * Clear all online statuses
     * Called when disconnecting from workspace
     */
    clearOnlineStatuses() {
      self.users.forEach(user => {
        user.isOnline = false;
      });
      console.log('[OnlinePresence] Cleared all online statuses');
    },
    
    /**
     * Update a user's cursor color
     * Called when user changes their color in settings
     */
    updateUserCursorColor(userId: string, color: string) {
      const user = self.users.get(userId);
      if (user) {
        user.cursorColor = color;
        console.log(`🎨 [OnlinePresence] Updated cursor color for ${userId}: ${color}`);
      }
    },
    
    /**
     * Update sessions from backend
     */
    updateSessions(sessions: any[] | undefined) {
      // Guard: Handle undefined/null sessions
      if (!sessions || !Array.isArray(sessions)) {
        console.warn(`⚠️  [OnlinePresence] updateSessions called with invalid data:`, sessions);
        return;
      }
      
      console.log(`📡 [OnlinePresence] Received ${sessions.length} sessions from backend`);
      if (sessions.length > 0) {
        console.log(`📊 [OnlinePresence] Sample session data:`, JSON.stringify(sessions[0], null, 2));
      }
      
      const sessionsByUser = new Map<string, any[]>();
      
      sessions.forEach(session => {
        const userId = session.userId;
        if (!sessionsByUser.has(userId)) {
          sessionsByUser.set(userId, []);
        }
        sessionsByUser.get(userId)!.push(session);
      });
      
      const currentUserIds = new Set(Array.from(self.users.keys()));
      
      sessionsByUser.forEach((userSessions, userId) => {
        let user = self.users.get(userId);
        
        // Get name and email from the first session (backend enriches this)
        const firstSession = userSessions[0];
        const userName = firstSession?.userName || null;
        const userEmail = firstSession?.userEmail || null;
        console.log(`👤 [OnlinePresence] User ${userId}: name="${userName}", email="${userEmail}"`);
        
        if (!user) {
          user = self.users.put({
            userId,
            email: userEmail,
            name: userName,
            firstName: null,
            lastName: null,
            avatar: null,
            cursorColor: '#4A9EFF',
            lastSeen: new Date(),
            sessionCount: 0,
            totalDurationSeconds: 0,
            isOnline: true,
            sessions: [],
          });
        } else {
          // Update name/email if backend provided them
          if (userName && !user.name) user.name = userName;
          if (userEmail && !user.email) user.email = userEmail;
        }
        
        user.isOnline = true;
        user.lastSeen = new Date();
        user.sessionCount = userSessions.length;
        
        user.sessions.clear();
        userSessions.forEach(session => {
          user.sessions.push({
            sessionId: session.sessionId,
            userId: session.userId,
            ip: session.ip || 'unknown',
            userAgent: session.userAgent || 'unknown',
            country: session.location?.country || null,
            region: session.location?.region || null,
            city: session.location?.city || null,
            timezone: session.location?.timezone || null,
            connectedAt: session.connectedAt || 0,
            lastPing: session.lastPing || 0,
          });
        });
        
        currentUserIds.delete(userId);
      });
      
      // Mark users without sessions as offline
      currentUserIds.forEach(userId => {
        const user = self.users.get(userId);
        if (user && user.isOnline) {
          user.isOnline = false;
          user.sessions.clear();
        }
      });
    },
  }));

export type IOnlinePresenceStore = Instance<typeof OnlinePresenceStore>;

